var XLSX = require("xlsx");
const parseJSON = str => str && typeof str === 'string' && /^[\[{]/.test(str.trim()) ? JSON.parse(str) : null;

export async function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary", cellStyles: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        let xls = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const headers = xls[0].map((header, idx, arr) => {
          if (!header) return "";
          let trimmed = header.trim();
          let count = arr.slice(0, idx).filter(h => h === trimmed).length;
          return count > 0 ? `${trimmed}_${count}` : trimmed;
        });

        xls.shift();

        resolve({ headers, data: xls });
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = (err) => reject(err);

    if (file.type === "text/csv") {
      reader.readAsText(file, "utf-8");
    } else {
      reader.readAsBinaryString(file);
    }
  });
}

// mapping-utils.js
export function mapFieldData({
                               elementName,
                               formData,
                               feed,
                               xls_feed_file_headers,
                               data,
                               MappingColumnSelectedOrder = {},
                               template_id,
                               index,
                               skip_numbered_fields = false,
                               map_parent_in_table = true
                             }) {
  const fieldName = elementName;

  // Skip fields with underscore and number if needed
  if (/_\d/.test(fieldName) && skip_numbered_fields) {
    return;
  }

  if (!formData.has(`mapping_fields[${fieldName}]`)) {
    let fieldMapping = feed[fieldName] !== undefined ? feed[fieldName] : '';
    let fieldHeaders = '';
    const additionalPropertyColumns = [];

    xls_feed_file_headers.forEach((header) => {
      if (header.trim().startsWith(fieldName + ', ')) {
        additionalPropertyColumns.push(header.replace(fieldName + ', ', ''));
      }
    });

    // Assuming data.template_fields[fieldName] exists
    const field_data = data.template_fields[fieldName];
    const fieldIndex = Object.keys(data.template_fields).indexOf(fieldName);

    // Transformation, conditionals, etc. — you need to implement your helper functions below
    const option_5 = field_data?.options?.Option5 || null;
    const conditional_rendering = option_5 ? JSON.parse(option_5) : null;

    if (conditional_rendering && conditional_rendering.conditions) {
      if (!evaluateConditions(feed, xls_feed_file_headers, conditional_rendering.conditions)) {
        return;
      }
    }

    const isNumberedField = isNumberedFieldCheck(template_id, fieldName, field_data);
    const isConditionalField = isConditionalFieldCheck(field_data);

    // Handle HTML type fields (simplified, adapt as needed)
    if (field_data?.type === 'HTML') {
      if (!map_parent_in_table) {
        return;
      }

      const mappings_for_this_row = MappingColumnSelectedOrder?.[template_id]?.[fieldIndex] && Array.isArray(fieldMapping)
        ? MappingColumnSelectedOrder[template_id][fieldIndex]
        : [];

      let values_array = [];
      let headers_array = [];

      mappings_for_this_row.forEach(mapping => {
        let fieldMappingRow = feed[mapping] !== undefined ? feed[mapping] : '';
        fieldMappingRow = String(fieldMappingRow).replaceAll(',', '|');
        const fieldMappingHeaders = xls_feed_file_headers[mapping] || '';
        values_array.push(fieldMappingRow);
        headers_array.push(fieldMappingHeaders);
      });

      if (!TablesMappingOrder[fieldName]) {
        TablesMappingOrder[fieldName] = [];
      }
      TablesMappingOrder[fieldName][0] = index;

      fieldMapping = values_array;
      fieldHeaders = headers_array;
    } else if (Array.isArray(fieldMapping) && fieldMapping.length > 1) {
      let combinedValues = '';
      fieldMapping.forEach(col => {
        const value = String(feed[col] !== undefined ? feed[col] : '');
        if (value.trim() !== '') {
          combinedValues += `• ${value.trim()}\n`;
        }
      });
      fieldMapping = combinedValues;
    } else if (isNumberedField && !isConditionalField) {
      if (!FieldsMappingOrder[fieldName]) {
        FieldsMappingOrder[fieldName] = [];
      }
      FieldsMappingOrder[fieldName][0] = index;
    }

    // Append to FormData
    formData.append(`mapping_fields[${fieldName}]`, fieldMapping);
    formData.append(`mapping_headers[${fieldName}]`, fieldHeaders);
    formData.append(`mapping_fields_transformation[${fieldName}]`, ''); // replace with actual transformation if any

    additionalPropertyColumns.forEach(additionalPropertyColumn => {
      const additionalPropertyColumnIndex = xls_feed_file_headers.indexOf(`${fieldName}, ${additionalPropertyColumn}`);
      if (feed[additionalPropertyColumnIndex] !== undefined) {
        formData.append(`mapping_fields_additional_properties[${fieldName}][${additionalPropertyColumn}]`, feed[additionalPropertyColumnIndex]);
      }
    });
  }
}

// Placeholder helpers - implement based on your logic
function evaluateConditions(feed, headers, conditions) {
  let result = evaluateSingleCondition(feed, headers, conditions[0]);

  for (let i = 1; i < conditions.length; i++) {
    const condition = conditions[i];
    const currentResult = evaluateSingleCondition(feed, headers, condition);

    if (condition.join === 'AND') {
      result = result && currentResult;
    } else if (condition.join === 'OR') {
      result = result || currentResult;
    }
  }
  return result;
}

function evaluateSingleCondition(feed, headers, condition) {
  const field = condition.field;
  const operator = condition.operator;
  const value = condition.value;
  const position = headers.indexOf(field);

  if (position !== -1) {
    switch (operator) {
      case '==': return feed[position] == value;
      case '!=': return feed[position] != value;
      case '>': return feed[position] > value;
      case '>=': return feed[position] >= value;
      case '<': return feed[position] < value;
      case '<=': return feed[position] <= value;
      default: return false;
    }
  } else {
    console.error("The field "+ field + " defined in the conditions of Option 5 for the template does not exist in the feed file");
    return true;
  }
}

function isNumberedFieldCheck(template_id, raw_fieldName, field_data, existingFieldNames = []) {
  const numberedFieldName = raw_fieldName + '_2';

  if (existingFieldNames.has ? existingFieldNames.has(numberedFieldName) : existingFieldNames.includes(numberedFieldName)) {
    return true;
  }

  return !!isConditionalFieldCheck(field_data);
}

function isConditionalFieldCheck(field_data){
  const option_5 = field_data.options.Option5;
  const conditional_rendering  = parseJSON (option_5)
  return !!(conditional_rendering && conditional_rendering.conditions);
}

// TablesMappingOrder and FieldsMappingOrder should be declared somewhere globally or passed in
const TablesMappingOrder = {};
const FieldsMappingOrder = {};

